import React, { useContext, useEffect, useReducer, useRef, useCallback } from 'react';
import {
	VideoStudiosContext,
	videoStudiosInitialState,
	IFilterValues,
	TVideoStudiosCountOfItems,
	TVideoStudiosSortBy,
	TTypeOfModalWindow,
} from './videoStudiosContext';
import { AppContext, IAppContext } from 'appState/appContext';
import { videoStudiosReducer } from './videoStudiosReducer';
import { VideoStudiosActionTypes } from './videoStudiosActions';
import { useFetchData } from 'hooks/useFetchData';
import { getPaginatedVideoStudios, deleteVideoStudio } from 'backend/videos';
import { AxiosResponse } from 'axios';
import { IVideoStudios, IGetPaginatedVideoStudios } from 'types';

const VideoStudiosState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(videoStudiosReducer, videoStudiosInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<AxiosResponse<IVideoStudios>, IGetPaginatedVideoStudios>(
		isMounted.current ? getPaginatedVideoStudios : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchByName: state.searchString ? state.searchString : null,
			sortByName: state.sortBy === 'NameA-Z' || state.sortBy === 'NameZ-A' ? state.sortBy : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: VideoStudiosActionTypes.SET_VIDEO_STUDIOS,
				payload: {
					videoStudios: response.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
		}
	}, [response]);

	const deleteSelectedItem = useCallback(async () => {
		try {
			showLoader();
			await deleteVideoStudio(state.IDForDeletion);
			const filteredVideoStudios = state.videoStudios.videoStudios.filter((el) => el.id !== state.IDForDeletion);
			dispatch({
				type: VideoStudiosActionTypes.SET_VIDEO_STUDIOS,
				payload: {
					videoStudios: {
						videoStudiosCount: state.videoStudios.videoStudiosCount - 1,
						videoStudios: filteredVideoStudios,
					},
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: VideoStudiosActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	}, [showLoader, hideLoader, state.IDForDeletion, state.videoStudios]);

	const changeSearchString = (value: string) => {
		dispatch({
			type: VideoStudiosActionTypes.CHANGE_SEARCH_STRING,
			payload: {
				searchString: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: VideoStudiosActionTypes.RESET_SELECTED_CONTROLS });

	const setSortBy = (sortBy: TVideoStudiosSortBy) => {
		dispatch({
			type: VideoStudiosActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setCountOfItems = (count: TVideoStudiosCountOfItems) => {
		dispatch({
			type: VideoStudiosActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: VideoStudiosActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const setIDForDeletion = (value: string) => {
		dispatch({
			type: VideoStudiosActionTypes.SET_ID_FOR_DELETION,
			payload: {
				IDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	};

	const setIDForEdit = (value: string) => {
		dispatch({
			type: VideoStudiosActionTypes.SET_ID_FOR_EDIT,
			payload: {
				IDForEdit: value,
				typeOfModalWindow: 'editStudio',
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: VideoStudiosActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setFiltersFromQueryString = (value: IFilterValues) => {
		dispatch({
			type: VideoStudiosActionTypes.SET_FILTERS_FROM_QUERY_STRING,
			payload: {
				filters: value,
			},
		});
	};

	const refetchStudios = (value: boolean) => {
		dispatch({
			type: VideoStudiosActionTypes.REFETCH_STUDIOS,
			payload: {
				needRefetchStudios: value,
			},
		});
	};

	useEffect(() => {
		isMounted.current = true;
		refetch();
	}, [state.currentPage, state.countOfItems, state.searchString, state.sortBy]);

	useEffect(() => {
		if (state.needRefetchStudios) {
			refetch();
			refetchStudios(false);
		}
	}, [state.needRefetchStudios]);

	return (
		<VideoStudiosContext.Provider
			value={{
				deleteSelectedItem,
				changeSearchString,
				resetSelectedControls,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setIDForDeletion,
				setIDForEdit,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				refetchStudios,
				videoStudios: state.videoStudios,
				searchString: state.searchString,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				IDForDeletion: state.IDForDeletion,
				IDForEdit: state.IDForEdit,
				typeOfModalWindow: state.typeOfModalWindow,
				needRefetchStudios: state.needRefetchStudios,
			}}>
			{children}
		</VideoStudiosContext.Provider>
	);
};

export default VideoStudiosState;
